import { graphql } from "@/__graphql__";
import PhoneNumber from "@/components/PhoneNumber";
import formatAddress from "@/functions/format-address";
import { EditOutlined, } from "@ant-design/icons";
import { Button, Card, Col, Row, Space } from "antd";
import dayjs from "dayjs";
import * as React from "react";
import { useNavigate } from "react-router";
import { useQuery } from "urql";
import InfoProp from "./InfoProp";
import { LocationsCard } from "./LocationsCard";

export default function CustomerDashboard() {
  const navigate = useNavigate();

  const [{ data, fetching: loading }, refetch] = useQuery({ query: RelationQuery });
  const [activeLocationId, setActiveLocationId] = React.useState<string | undefined>();

  React.useEffect(() => {
    if (undefined !== activeLocationId || undefined === data) return;
    setActiveLocationId(data.relation?.locations[0]?.id);
  }, [data]);

  const handleOnLocationClick = (locationId: string) => {
    setActiveLocationId(locationId);
  };

  const handleOnEditClick = () => {
    navigate(`/locations/${activeLocationId}/edit`);
  };

  const handleOnMapClick = () => {
    navigate("/locations");
  };

  const relation = data?.relation;
  const contract = data?.contract;

  // eslint-disable-next-line prettier/prettier
  const currentLocation =
    undefined !== activeLocationId
      ? data?.relation?.locations.find((l) => l.id === activeLocationId)
      : undefined;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gridTemplateRows: "masonry",
        columnGap: 16,
        rowGap: 16,
      }}
    >
      <Space orientation="vertical" size={16}>
        <Card title="Hartelijk welkom op het klantportaal van Defibrion!">
          <p>
            Hier vindt u alle informatie rondom uw veiligheidsmiddelen die in
            onderhoud zijn bij Defibrion. Informatie over de planning van de
            bezoekafspraken, de status en gegevens van alle veiligheidsmiddelen
            en de onderhoudsrapportages. Via de berichtenmodule kunt u
            communiceren met de serviceafdeling.
          </p>

          <p>
            De helpdesk is telefonische te bereiken op:{" "}
            <PhoneNumber>050 3116085</PhoneNumber> (voor Nederland) en{" "}
            <PhoneNumber>037 470 079</PhoneNumber> (voor België)
          </p>
        </Card>
        <Card loading={loading} title="Klantgegevens">
          {undefined !== relation && (
            <Row justify="space-between" gutter={4}>
              <Col span={24 / 3}>
                <InfoProp label="Naam" value={relation?.name} />
                <InfoProp label="Vestigingslocatie" value={relation?.place} />
              </Col>
              <Col span={24 / 3}>
                <InfoProp label="Klantcode" value={relation?.afasCode} />
                <InfoProp
                  label="Aantal locaties"
                  value={relation?.locations.length}
                />
              </Col>
              <Col span={24 / 3}>
                <InfoProp
                  label="Startdatum contract"
                  value={
                    contract?.startingOn
                      ? dayjs(contract.startingOn).format("MM/YYYY")
                      : "-"
                  }
                />
                <InfoProp
                  label="Einddatum contract"
                  value={
                    contract?.endingOn
                      ? dayjs(contract.endingOn).format("MM/YYYY")
                      : "-"
                  }
                />
              </Col>
            </Row>
          )}
        </Card>
      </Space>
      <Space orientation="vertical" size={16}>
        <LocationsCard
          selectedId={activeLocationId}
          defaultAreaCoverageInMeters={
            relation?.defaultAreaCoverageInMeters ?? undefined
          }
          loading={loading}
          locations={relation?.locations ?? []}
          onClick={handleOnLocationClick}
          onMapClick={handleOnMapClick}
        />
        {undefined !== currentLocation && (
          <Card
            actions={[
              <Button
                key="edit-button"
                onClick={() => handleOnEditClick()}
                icon={<EditOutlined />}
                type="text"
              >
                Contactgegevens aanpassen
              </Button>,
            ]}
            title={`Geselecteerde locatie: ${currentLocation.name}`}
          >
            <Row justify="space-between" gutter={4}>
              <Col span={24 / 2}>
                {currentLocation.contactPersons.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <strong style={{ display: "block", marginBottom: 4 }}>
                      Contactpersonen
                    </strong>
                    {currentLocation.contactPersons.map((cp) => (
                      <div key={cp.id} style={{ marginBottom: 8 }}>
                        {cp.contactPerson.name}
                      </div>
                    ))}
                  </div>
                )}
                <InfoProp
                  label="Adres"
                  value={formatAddress(currentLocation.address)}
                />
              </Col>
              <Col span={24 / 2}>
                <InfoProp
                  label="Taalinstelling"
                  value={
                    locales[currentLocation.locale as keyof typeof locales]
                  }
                />
              </Col>
            </Row>
          </Card>
        )}
      </Space>
    </div>
  );
}

const locales = {
  nl_NL: "Nederlands (Nederland)",
  nl_BE: "Nederlands (Belgie)",
  fr_BE: "Frans (Belgie)",
};

const RelationQuery = graphql(`
  query Dashboard__CustomerDashboard__RelationQuery {
    relation {
      id
      name
      place
      afasCode
      isDealer
      defaultAreaCoverageInMeters
      locations {
        id
        name
        contactPersons {
          id
          contactPerson {
            id
            name
            emailAddresses
            phoneNumbers
          }
        }
        locale
        address {
          street
          postalCode
          city
          country
          coordinates {
            latitude
            longitude
          }
        }
        deletedAt
      }
    }
    contract {
      id
      startingOn
      endingOn
    }
  }
`);
